import type { ContentHistory, ContentHistoryParams, HistoryTargetOptions, HistoryTargetParams } from './content-history-types'
import { request } from '../api/client.js'
import type {
  ActivityListResponse,
  EntityActivityParams,
  MyActivityParams,
} from './types.js'

const ROOT = '/coach/admin/activity'

function withParams(base: string, entries: Array<[string, string | undefined]>): string {
  const query = new URLSearchParams()
  for (const [key, value] of entries) {
    if (value !== undefined && value !== '') query.set(key, value)
  }
  return query.size > 0 ? `${base}?${query.toString()}` : base
}

export const activityApi = {
  contentHistory(params: ContentHistoryParams): Promise<ContentHistory> {
    return request<ContentHistory>(withParams(`${ROOT}/content-history`, Object.entries(params)), { method: 'GET' })
  },
  historyTargets(params: HistoryTargetParams): Promise<HistoryTargetOptions> {
    return request<HistoryTargetOptions>(withParams(`${ROOT}/content-history/targets`, Object.entries(params)), { method: 'GET' })
  },
  /** One OPERATOR's audited actions, newest first — the caller's own
   * by default; a colleague's via `actorId` (admin identities only —
   * the approved peer-inspection rule). */
  mine(params: MyActivityParams = {}): Promise<ActivityListResponse> {
    return request<ActivityListResponse>(
      withParams(ROOT, [
        ['actorId', params.actorId],
        ['family', params.family],
        ['operation', params.operation],
        ['entityType', params.entityType],
        ['clientId', params.clientId],
        ['since', params.since],
        ['until', params.until],
        ['limit', params.limit === undefined ? undefined : String(params.limit)],
        ['offset', params.offset === undefined ? undefined : String(params.offset)],
      ]),
      { method: 'GET' },
    )
  },

  /** One entity's projected timeline, newest first. */
  entity(params: EntityActivityParams): Promise<ActivityListResponse> {
    return request<ActivityListResponse>(
      withParams(`${ROOT}/entity`, [
        ['entityType', params.entityType],
        ['entityId', params.entityId],
        ['clientId', params.clientId],
        ['family', params.family],
        ['operation', params.operation],
        ['systemEvents', params.systemEvents],
        ['since', params.since],
        ['until', params.until],
        ['limit', params.limit === undefined ? undefined : String(params.limit)],
        ['offset', params.offset === undefined ? undefined : String(params.offset)],
      ]),
      { method: 'GET' },
    )
  },
}

export function messageForActivityError(err: unknown): string {
  const detail =
    typeof err === 'object' && err !== null && 'detail' in err
      ? (err as { detail?: unknown }).detail
      : undefined
  switch (detail) {
    case 'program_context_required':
      return 'Choose a Program context for this diagnostic or tutor.'
    case 'program_context_only_for_diagnostics':
      return 'This lesson uses its recorded parent Program. Select the lesson again to clear the extra context.'
    case 'client_id_required':
      return 'This history needs its client context — reload the page.'
    case 'admin_required':
      return 'Your session does not have operator access.'
    default:
      return 'Could not load activity. Try again.'
  }
}
